import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  type TextInput,
  type TextInputFocusEventData,
  type TextInputKeyPressEventData,
} from "react-native";
import {
  type FontTokens,
  type GetProps,
  getFontSize,
  getTokens,
  styled,
  Input as TamaguiInput,
  type InputProps as TamaguiInputProps,
  useTheme,
  View,
  XStack,
} from "tamagui";
import { Body } from "./Body";
import type { IconProp } from "./icons";
import { LoadingCircular } from "./LoadingCircular";

const StyledInput = styled(TamaguiInput);

type Props = {
  type?: "padded" | "inline" | "border";
  error?: boolean;
  attention?: boolean;
  submitOnEnter?: boolean;
  submitOnBlur?: boolean;
  preventNewLine?: boolean;
  clearOnSubmit?: boolean;
  allowEmptyTextSubmit?: boolean;
  onExtraKeyPress?: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => boolean;
  icon?: IconProp;
  iconText?: string;
  iconFirst?: boolean;
  label?: string;
  onIconPress?: () => void;
  focusOnIconPress?: boolean;
  isLoading?: boolean;
} & TamaguiInputProps;

const InputBase = StyledInput.styleable<Props>(
  React.forwardRef((props, ref) => {
    const innerRef = useRef<TextInput>(null);
    const {
      multiline = true,
      submitOnEnter,
      submitOnBlur = submitOnEnter,
      preventNewLine = submitOnEnter,
      clearOnSubmit,
      onChangeText,
      enterKeyHint,
      defaultValue,
      allowEmptyTextSubmit,
      icon: Icon,
      iconFirst,
      iconText,
      onFocus,
      onBlur,
      onExtraKeyPress,
      onIconPress,
      value,
      focusOnIconPress,
      isLoading,
      ...rest
    } = props;

    const theme = useTheme();
    const [text, setText] = useState(defaultValue ?? value ?? "");
    const [submittedText, setSubmittedText] = useState(
      defaultValue ?? value ?? "",
    );

    const _tokens = getTokens();
    const fontSizeValue = getFontSize(undefined, {
      font: (props.fontFamily as FontTokens) ?? "$body",
    });

    const lineHeight = fontSizeValue * 1.25;

    const handleChangeText = useCallback(
      (changedText: string) => {
        onChangeText?.(changedText);
        setText(changedText);
      },
      [onChangeText],
    );

    const handleChange = useCallback(
      (e: { nativeEvent: { text: string; target: unknown } }) => {
        e.nativeEvent.text = preventNewLine
          ? e.nativeEvent.text.replaceAll("\n", "")
          : e.nativeEvent.text;

        handleChangeText(e.nativeEvent.text);

        if (!multiline || Platform.OS !== "web") return;

        const target = e.nativeEvent.target as HTMLElement;

        window.requestAnimationFrame(() => {
          target.style.height = "0px";
          target.style.height = `${Math.max(target.scrollHeight, lineHeight)}px`;
        });
      },
      [handleChangeText, preventNewLine, lineHeight, multiline],
    );

    useImperativeHandle(ref, () => {
      return {
        focus: () => innerRef.current?.focus(),
        blur: () => innerRef.current?.blur(),
        clear: () => {
          handleChange({ nativeEvent: { text: "", target: innerRef.current } });
        },
      };
    });

    useEffect(() => {
      setText(value ?? "");
    }, [value]);

    const handleSubmit = useCallback(
      (e: NativeSyntheticEvent<unknown>) => {
        if (!submitOnEnter && submittedText === text) {
          return;
        }

        if (!text && !allowEmptyTextSubmit) {
          return;
        }

        props.onSubmitEditing?.({
          ...e,
          nativeEvent: { text },
        });

        setSubmittedText(text);

        if (clearOnSubmit) {
          handleChange({
            ...e,
            nativeEvent: {
              text: "",
              target: innerRef.current,
            },
          });
        }

        if (rest.blurOnSubmit) {
          innerRef.current?.blur();
        }
      },
      [
        allowEmptyTextSubmit,
        submitOnEnter,
        text,
        submittedText,
        props.onSubmitEditing,
        clearOnSubmit,
        rest.blurOnSubmit,
        handleChange,
      ],
    );

    const handleBlur = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        if (submitOnBlur) {
          handleSubmit(e);
        }

        onBlur?.(e);
      },
      [handleSubmit, submitOnBlur, onBlur],
    );

    const onKeyPress = useCallback(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (onExtraKeyPress?.(e)) {
          // If returning true we skip the rest
          return;
        }

        if (submitOnEnter && e.nativeEvent.key === "Enter") {
          handleSubmit(e);
          e.preventDefault();
        }
      },
      [handleSubmit, submitOnEnter, onExtraKeyPress],
    );

    const handleFocus = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        onFocus?.(e);
      },
      [onFocus],
    );

    const focus = useCallback(() => {
      innerRef.current?.focus();
    }, []);

    useEffect(() => {
      if (defaultValue) {
        setText(defaultValue ?? "");
      }
    }, [defaultValue]);

    useEffect(() => {
      const element = innerRef.current as unknown as HTMLElement;
      if (multiline && Platform.OS === "web" && !!element) {
        window.requestAnimationFrame(() => {
          element.style.height = "0px";
          element.style.height = `${Math.max(
            element.scrollHeight,
            lineHeight,
          )}px`;
        });
      }
    }, [lineHeight, multiline]);

    const iconWidth = 24;
    const iconMargin = 12;

    return (
      <View position="relative" flex={rest.flex ?? 1}>
        {!!Icon && iconFirst && (
          <View
            position="absolute"
            top={0}
            bottom={0}
            left={0}
            alignItems="center"
            justifyContent="center"
            paddingLeft={props.type === "inline" ? 0 : 23}
          >
            {Icon && (
              <Pressable onPress={onIconPress || handleSubmit}>
                <Icon size={24} color={theme.muted} />
              </Pressable>
            )}
          </View>
        )}

        <TamaguiInput
          ref={innerRef}
          unstyled
          backgroundColor={"$backgroundBlock"}
          {...rest}
          multiline={multiline}
          onKeyPress={onKeyPress}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={text}
          enterKeyHint={(enterKeyHint ?? submitOnEnter) ? "done" : "enter"}
          style={[
            {
              fontSize: fontSizeValue,
              lineHeight,
              width: "100%",
            },
            Icon && iconFirst
              ? {
                  paddingLeft:
                    props.type === "inline"
                      ? iconWidth + iconMargin
                      : iconWidth + iconMargin + 23,
                }
              : undefined,
            Icon && !iconFirst
              ? {
                  paddingRight:
                    props.type === "inline"
                      ? iconWidth + iconMargin
                      : iconWidth + iconMargin + 23,
                }
              : undefined,
          ]}
          onSubmitEditing={handleSubmit}
        />

        {!!Icon && !iconFirst && (
          <View
            position="absolute"
            top={0}
            bottom={0}
            right={0}
            alignItems="center"
            justifyContent="center"
            cursor={"pointer"}
            paddingRight={props.type === "inline" ? 0 : 23}
            onPress={focusOnIconPress ? focus : onIconPress || handleSubmit}
          >
            {Icon && (
              <XStack gap={"$small"}>
                {!!iconText && <Body muted>{iconText}</Body>}
                <Icon size={24} color={theme.muted} />
              </XStack>
            )}
          </View>
        )}

        {isLoading && (
          <View
            position="absolute"
            top={0}
            bottom={0}
            right={props.type === "inline" ? 0 : 23}
            width={24}
            alignItems="center"
            justifyContent="center"
            backgroundColor={"#fff"}
          >
            <LoadingCircular />
          </View>
        )}
      </View>
    );
  }),
);

export const Input = styled(
  InputBase,
  {
    borderWidth: 0.1, // any value is required for auto sizing to work + borderColor transparent
    borderColor: "transparent",
    overflow: "hidden",
    width: "100%",
    fontWeight: "300",
    color: "$color",
    placeholderTextColor: "$muted",
    fontFamily: "$body",

    defaultVariants: {
      type: "inline",
    },
    variants: {
      type: {
        inline: {
          focusStyle: {
            outlineOffset: 5,
            outlineColor: "$divider",
            outlineStyle: "dashed",
            outlineWidth: 1,
          },
        },
        padded: {
          paddingHorizontal: 23,
          paddingVertical: 21,
          focusStyle: {
            outlineColor: "$divider",
            outlineStyle: "dashed",
            outlineWidth: 1,
          },
        },
        border: {
          paddingHorizontal: 23,
          paddingVertical: 15,
          borderWidth: 1,
          borderColor: "$divider",
          focusStyle: {
            outlineColor: "$divider",
            outlineStyle: "dashed",
            outlineWidth: 1,
          },
        },
      },
      error: {
        true: {
          borderBottomWidth: 1,
          borderBottomColor: "$danger",
        },
      },
      attention: {
        true: {
          borderBottomWidth: 1,
          borderBottomColor: "$info",
        },
      },
    },
  } as const,
  {
    inlineProps: new Set(["type", "error", "attention"]),
    isInput: true,
    accept: {
      placeholderTextColor: "color",
      selectionColor: "color",
    } as const,
  },
);

type InputType = typeof Input;

export type InputProps = GetProps<InputType>;
